/**
 * Formatting shared by the customer broadcast pages.
 *
 * Its own module rather than an export from one of the pages: the list, the
 * brands page and the details page all need these, and importing between
 * sibling components is how this feature has already produced `undefined`
 * component renders once.
 */

/** Longest sensible unit first. Null is a broadcast whose probe never ran. */
export const formatDuration = (ms: number | null): string => {
  if (!ms) return '—'
  const total = Math.round(ms / 1000)
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  return m < 60
    ? `${m}m ${String(total % 60).padStart(2, '0')}s`
    : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

/**
 * `event_date` is a DATE, not a timestamp. Handing it to `new Date()` parses it
 * as UTC midnight, which renders a day early anywhere west of Greenwich.
 */
export const formatEventDate = (d: string | null): string | null => {
  if (!d) return null
  const [y, m, day] = d.slice(0, 10).split('-').map(Number)
  if (!y || !m || !day) return null
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric'
  })
}

const CHANNEL_TYPES: Record<string, string> = {
  linear: 'Linear TV',
  svod: 'Subscription streaming',
  fast: 'FAST channel',
  free_stream: 'Free stream',
  social: 'Social',
  other: 'Other'
}

export const formatChannelType = (t: string | null | undefined): string | null =>
  t ? CHANNEL_TYPES[t] ?? t : null

/** 31200 -> "31.2K". Compact for tables; the full figure goes in tooltips. */
export const formatViewers = (n: number | string | null | undefined): string | null => {
  if (n == null || n === '') return null
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n))
}

/** "31,200 viewers (Internal estimate, estimated)". Null when no audience. */
export const describeViewers = (b: {
  viewers: number | null
  viewership_source_name?: string | null
  viewers_is_estimate?: boolean | null
}): string | null => {
  if (b.viewers == null) return null
  const src = b.viewership_source_name ?? null
  // "Internal estimate" already says it; do not append "estimate" twice.
  const est = b.viewers_is_estimate && !src?.toLowerCase().includes('estimate') ? 'estimate' : null
  const parts = [src, est].filter(Boolean)
  return `${Number(b.viewers).toLocaleString()} viewers${parts.length ? ` (${parts.join(', ')})` : ''}`
}
